import { useDialogFocus } from "hooks/useDialogFocus";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";

import LineIcon from "./LineIcon";
import type { MilestoneCelebrationProps } from "./milestoneCelebration.types";

import styles from "./MilestoneCelebration.module.css";

/**
 * The moment a tree reaches five connected nodes — the point at which it has actually become
 * something. Until now that moment passed unmarked: the fifth node landed exactly like the fourth.
 *
 * It is a handoff, not a trophy. Someone who has just built something is the likeliest they will
 * ever be to show it to a relative, so the primary action moves them towards sharing; the secondary
 * simply returns them to the canvas. Neither is destructive, so Escape dismisses freely.
 */
const MilestoneCelebration = ({ nodeCount, onKeepBuilding, onShare, treeTitle }: MilestoneCelebrationProps) => {
	const { t } = useTranslation("homePage");
	const dialogRef = useDialogFocus<HTMLDivElement>();

	useEffect(() => {
		const onKeyDown = (event: KeyboardEvent): void => {
			if (event.key === "Escape") {
				onKeepBuilding();
			}
		};

		document.addEventListener("keydown", onKeyDown);

		return () => document.removeEventListener("keydown", onKeyDown);
	}, [onKeepBuilding]);

	return (
		<div className={styles.overlay}>
			<div
				ref={dialogRef}
				aria-labelledby="milestone-title"
				aria-modal="true"
				className={styles.dialog}
				role="dialog">
				<span aria-hidden="true" className={styles.badge}>
					<LineIcon name="sparkle" />
				</span>

				<header className={styles.header}>
					<h2 id="milestone-title">{t("milestone.title")}</h2>
					<p className={styles.subtitle}>{t("milestone.subtitle", { count: nodeCount, title: treeTitle })}</p>
				</header>

				<p className={styles.prompt}>{t("milestone.prompt")}</p>

				<footer className={styles.footer}>
					<button className={styles.secondaryButton} onClick={onKeepBuilding} type="button">
						{t("milestone.keepBuilding")}
					</button>
					<button className={styles.primaryButton} onClick={onShare} type="button">
						{t("milestone.share")}
					</button>
				</footer>
			</div>
		</div>
	);
};

export default MilestoneCelebration;
